import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Receipt, RefreshCw, FileText, Download, CheckCircle2, XCircle, AlertTriangle,
  Trash2, Plus, Eye, Lock, History, PlayCircle, X, Link2Off
} from 'lucide-react';
import { UserProfile, Property, CrgStatement, CrgLine, CrgGenerationResult, PipelineType } from '../types';
import { storage } from '../services/storage';
import { billingService, computeCrgTotals } from '../services/billingService';
import { rentalReadyService } from '../services/rentalReadyService';
import { CrgPdfDocument } from './CrgPdfDocument';
import { useModal } from './ui/ModalProvider';

interface BillingPanelProps {
  userProfile: UserProfile | null;
  selectedAgencyId?: string | null;
}

const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const fmtEur = (n: number) => (n || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const fmtDate = (iso?: string) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

// Mois précédent par défaut (le CRG se génère début M+1)
const defaultMonthValue = () => {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
};

const OUTCOME_LABELS: Record<string, string> = {
  created: 'Brouillon créé',
  updated: 'Brouillon régénéré',
  skipped_finalized: 'Ignoré (déjà finalisé)',
  skipped_unlinked: 'Ignoré (non lié à RentalReady)',
  skipped_no_rate: 'Ignoré (aucun taux de commission)',
  error: 'Erreur',
};

export const BillingPanel: React.FC<BillingPanelProps> = ({ userProfile, selectedAgencyId }) => {
  const { toast, confirm } = useModal();
  const [view, setView] = useState<'generate' | 'history'>('generate');
  const [monthValue, setMonthValue] = useState(defaultMonthValue());
  const [year, month] = monthValue.split('-').map(Number);

  const [properties, setProperties] = useState<Property[]>([]);
  const [contractRates, setContractRates] = useState<Record<string, number | null>>({});
  const [statements, setStatements] = useState<CrgStatement[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastResults, setLastResults] = useState<CrgGenerationResult[]>([]);

  // Revue de brouillon
  const [reviewStatement, setReviewStatement] = useState<CrgStatement | null>(null);

  // Historique
  const [historyYear, setHistoryYear] = useState<number>(new Date().getFullYear());
  const [historyStatements, setHistoryStatements] = useState<CrgStatement[]>([]);

  // Rendu PDF caché
  const [pdfJob, setPdfJob] = useState<{ statement: CrgStatement; action: 'download' | 'finalize' } | null>(null);
  const pdfRef = useRef<HTMLDivElement>(null);

  const agencyId = selectedAgencyId || null;

  const eligibleProperties = properties.filter(
    p => p.status === 'MANAGED' && p.contractType === PipelineType.CONCIERGE
  );

  const statementByProperty = new Map(statements.map(s => [s.propertyId, s]));

  const effectiveRate = (p: Property): number | null =>
    p.commissionRateOverride ?? contractRates[p.id || ''] ?? null;

  const loadData = useCallback(async () => {
    if (!agencyId) return;
    setLoading(true);
    try {
      const [props, stmts] = await Promise.all([
        storage.getProperties(agencyId),
        billingService.getCrgStatements(agencyId, { year, month }),
      ]);
      setProperties(props);
      setStatements(stmts);

      // Taux contractuels (pour les biens sans override)
      const eligible = props.filter(p => p.status === 'MANAGED' && p.contractType === PipelineType.CONCIERGE);
      const rates: Record<string, number | null> = {};
      await Promise.all(
        eligible
          .filter(p => p.id && p.commissionRateOverride == null)
          .map(async p => { rates[p.id!] = await billingService.getContractCommissionRate(p.id!); })
      );
      setContractRates(rates);
    } finally {
      setLoading(false);
    }
  }, [agencyId, year, month]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadHistory = useCallback(async () => {
    if (!agencyId) return;
    const stmts = await billingService.getCrgStatements(agencyId, { year: historyYear });
    setHistoryStatements(stmts);
  }, [agencyId, historyYear]);

  useEffect(() => { if (view === 'history') loadHistory(); }, [view, loadHistory]);

  // --- Sélection ---
  const isSelectable = (p: Property) => {
    const stmt = statementByProperty.get(p.id!);
    return !!p.rrRentalId && effectiveRate(p) != null && stmt?.status !== 'FINALIZED';
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    const valid = eligibleProperties.filter(isSelectable).map(p => p.id!);
    setSelectedIds(prev => (prev.size === valid.length ? new Set() : new Set(valid)));
  };

  // --- Génération ---
  const handleGenerate = async () => {
    if (!agencyId || selectedIds.size === 0) return;
    const hasDrafts = [...selectedIds].some(id => statementByProperty.get(id)?.status === 'DRAFT');
    if (hasDrafts) {
      const ok = await confirm({
        title: 'Régénérer les brouillons existants ?',
        message: 'Certains biens sélectionnés ont déjà un brouillon pour ce mois. La régénération remplacera leurs lignes (les ajustements manuels seront perdus). Continuer ?',
        confirmText: 'Régénérer',
        cancelText: 'Annuler',
      });
      if (!ok) return;
    }

    setGenerating(true);
    setLastResults([]);
    try {
      const results = await rentalReadyService.generateCrg(agencyId, year, month, [...selectedIds]);
      setLastResults(results);
      const okCount = results.filter(r => r.outcome === 'created' || r.outcome === 'updated').length;
      const errCount = results.filter(r => r.outcome === 'error').length;
      if (okCount) toast.success(`${okCount} CRG généré(s) en brouillon`);
      if (errCount) toast.error(`${errCount} bien(s) en erreur`);
      setSelectedIds(new Set());
      await loadData();
    } catch (e: any) {
      toast.error('Erreur de génération : ' + (e.message || 'Erreur inconnue'));
    } finally {
      setGenerating(false);
    }
  };

  // --- PDF ---
  const runPdfJob = useCallback(async () => {
    if (!pdfJob || !pdfRef.current) return;
    const { statement, action } = pdfJob;
    const monthLabel = `${statement.periodYear}-${String(statement.periodMonth).padStart(2, '0')}`;
    const fileName = `CRG_${(statement.headerSnapshot.propertyName || 'bien').replace(/[^a-zA-Z0-9]/g, '_')}_${monthLabel}.pdf`;
    const options = {
      margin: 0,
      filename: fileName,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true, backgroundColor: '#ffffff', logging: false, scrollY: 0 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait', compress: true },
      pagebreak: { mode: ['css', 'legacy'] },
    };

    try {
      // @ts-ignore
      if (!window.html2pdf) throw new Error('html2pdf non disponible');
      if (action === 'download') {
        // @ts-ignore
        await window.html2pdf().set(options).from(pdfRef.current.firstElementChild).save();
        toast.success('PDF téléchargé');
      } else {
        // @ts-ignore
        const blob: Blob = await window.html2pdf().set(options).from(pdfRef.current.firstElementChild).output('blob');
        const path = await billingService.uploadCrgPdf(statement.propertyId, statement.periodYear, statement.periodMonth, blob);
        await billingService.finalizeCrg(statement.id, path);
        toast.success('CRG finalisé et archivé');
        setReviewStatement(null);
        await loadData();
        if (view === 'history') await loadHistory();
      }
    } catch (e: any) {
      toast.error('Erreur PDF : ' + (e.message || 'Erreur inconnue'));
    } finally {
      setPdfJob(null);
    }
  }, [pdfJob, loadData, loadHistory, view, toast]);

  useEffect(() => {
    if (pdfJob) {
      // Laisser le DOM caché se peindre avant la rasterisation
      const timer = setTimeout(runPdfJob, 150);
      return () => clearTimeout(timer);
    }
  }, [pdfJob, runPdfJob]);

  const handleFinalize = async (statement: CrgStatement) => {
    const ok = await confirm({
      title: 'Finaliser ce CRG ?',
      message: `Le CRG de ${MONTHS_FR[statement.periodMonth - 1]} ${statement.periodYear} sera définitivement figé et son PDF archivé. Aucune modification ne sera possible ensuite. Continuer ?`,
      confirmText: 'Finaliser',
      cancelText: 'Annuler',
    });
    if (!ok) return;
    setPdfJob({ statement, action: 'finalize' });
  };

  const handleDownloadArchived = async (statement: CrgStatement) => {
    if (!statement.pdfStoragePath) return;
    const url = await billingService.getPdfUrl(statement.pdfStoragePath);
    if (url) window.open(url, '_blank', 'noopener');
    else toast.error('Impossible de générer le lien de téléchargement');
  };

  if (!agencyId) {
    return (
      <div className="p-8">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-amber-800">
          Sélectionnez une agence pour accéder à la facturation.
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 h-full overflow-y-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <Receipt className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Facturation</h1>
            <p className="text-sm text-slate-500">Comptes rendus de gestion (CRG) — biens en conciergerie</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setView('generate')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${view === 'generate' ? 'bg-white text-slate-900 shadow' : 'text-slate-600 hover:text-slate-800'}`}
            >
              <PlayCircle className="w-4 h-4" /> Génération
            </button>
            <button
              onClick={() => setView('history')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${view === 'history' ? 'bg-white text-slate-900 shadow' : 'text-slate-600 hover:text-slate-800'}`}
            >
              <History className="w-4 h-4" /> Historique
            </button>
          </div>
        </div>
      </div>

      {view === 'generate' && (
        <>
          {/* Sélecteur de mois + action */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <label className="text-sm font-bold text-slate-700">Mois à facturer :</label>
              <input
                type="month"
                value={monthValue}
                onChange={e => { setMonthValue(e.target.value); setSelectedIds(new Set()); setLastResults([]); }}
                className="p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm"
              />
              {loading && <RefreshCw className="w-4 h-4 animate-spin text-slate-400" />}
            </div>
            <button
              onClick={handleGenerate}
              disabled={generating || selectedIds.size === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-lg font-bold shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generating ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /> Génération en cours...</>
              ) : (
                <><PlayCircle className="w-4 h-4" /> Générer les CRG ({selectedIds.size})</>
              )}
            </button>
          </div>

          {/* Résultats de la dernière génération */}
          {lastResults.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6">
              <p className="text-sm font-bold text-slate-700 mb-2">Résultats :</p>
              <ul className="space-y-1">
                {lastResults.map((r, i) => (
                  <li key={i} className="text-xs text-slate-600 flex items-center gap-2">
                    {r.outcome === 'created' || r.outcome === 'updated' ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    ) : r.outcome === 'error' ? (
                      <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                    ) : (
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    )}
                    <span className="font-medium">{r.propertyName || r.propertyId}</span>
                    <span>— {OUTCOME_LABELS[r.outcome] || r.outcome}{r.message ? ` : ${r.message}` : ''}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Tableau des biens */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="p-3 w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.size > 0 && selectedIds.size === eligibleProperties.filter(isSelectable).length}
                      onChange={selectAll}
                      className="w-4 h-4 text-emerald-600 border-slate-300 rounded"
                    />
                  </th>
                  <th className="p-3 text-left text-xs font-bold text-slate-500 uppercase">Bien</th>
                  <th className="p-3 text-left text-xs font-bold text-slate-500 uppercase">Lien RentalReady</th>
                  <th className="p-3 text-left text-xs font-bold text-slate-500 uppercase">Taux</th>
                  <th className="p-3 text-left text-xs font-bold text-slate-500 uppercase">Ménage refacturé</th>
                  <th className="p-3 text-left text-xs font-bold text-slate-500 uppercase">CRG {MONTHS_FR[month - 1]} {year}</th>
                  <th className="p-3 w-32"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {eligibleProperties.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-slate-400 text-sm">
                      Aucun bien en gestion de type Conciergerie pour cette agence.
                    </td>
                  </tr>
                )}
                {eligibleProperties.map(p => {
                  const stmt = statementByProperty.get(p.id!);
                  const rate = effectiveRate(p);
                  const selectable = isSelectable(p);
                  return (
                    <tr key={p.id} className={selectable ? '' : 'bg-slate-50 opacity-70'}>
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(p.id!)}
                          onChange={() => toggleSelect(p.id!)}
                          disabled={!selectable}
                          className="w-4 h-4 text-emerald-600 border-slate-300 rounded disabled:cursor-not-allowed"
                        />
                      </td>
                      <td className="p-3">
                        <p className="text-sm font-bold text-slate-900">{p.name || p.address}</p>
                        <p className="text-xs text-slate-500">{p.city}</p>
                      </td>
                      <td className="p-3">
                        {p.rrRentalId ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold">
                            <CheckCircle2 className="w-3 h-3" /> {p.rrRentalName || p.rrRentalId}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">
                            <Link2Off className="w-3 h-3" /> Non lié
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        {rate != null ? (
                          <span className="text-sm font-bold text-slate-900">
                            {rate} %
                            <span className="text-xs font-normal text-slate-400 ml-1">
                              {p.commissionRateOverride != null ? '(personnalisé)' : '(contrat)'}
                            </span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600">
                            <AlertTriangle className="w-3 h-3" /> Aucun taux
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-sm text-slate-600">{p.rebillCleaning ? 'Oui' : 'Non'}</td>
                      <td className="p-3">
                        {!stmt ? (
                          <span className="text-xs text-slate-400">—</span>
                        ) : stmt.status === 'FINALIZED' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-800 text-white rounded-full text-xs font-bold">
                            <Lock className="w-3 h-3" /> Finalisé · {fmtEur(stmt.totalBilled)}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                            <FileText className="w-3 h-3" /> Brouillon · {fmtEur(stmt.totalBilled)}
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        {stmt && stmt.status === 'DRAFT' && (
                          <button
                            onClick={() => setReviewStatement(stmt)}
                            className="text-xs font-bold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
                          >
                            <Eye className="w-3.5 h-3.5" /> Réviser
                          </button>
                        )}
                        {stmt && stmt.status === 'FINALIZED' && stmt.pdfStoragePath && (
                          <button
                            onClick={() => handleDownloadArchived(stmt)}
                            className="text-xs font-bold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
                          >
                            <Download className="w-3.5 h-3.5" /> PDF
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {view === 'history' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex items-center gap-3">
            <label className="text-sm font-bold text-slate-700">Année :</label>
            <select
              value={historyYear}
              onChange={e => setHistoryYear(Number(e.target.value))}
              className="p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm"
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="p-3 text-left text-xs font-bold text-slate-500 uppercase">Période</th>
                <th className="p-3 text-left text-xs font-bold text-slate-500 uppercase">Bien</th>
                <th className="p-3 text-left text-xs font-bold text-slate-500 uppercase">Propriétaire</th>
                <th className="p-3 text-right text-xs font-bold text-slate-500 uppercase">Total facturé</th>
                <th className="p-3 text-left text-xs font-bold text-slate-500 uppercase">Statut</th>
                <th className="p-3 w-40"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {historyStatements.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-400 text-sm">Aucun CRG pour {historyYear}.</td>
                </tr>
              )}
              {historyStatements.map(s => (
                <tr key={s.id}>
                  <td className="p-3 text-sm font-bold text-slate-900">{MONTHS_FR[s.periodMonth - 1]} {s.periodYear}</td>
                  <td className="p-3 text-sm text-slate-700">{s.headerSnapshot.propertyName || s.headerSnapshot.propertyAddress || '—'}</td>
                  <td className="p-3 text-sm text-slate-700">{s.headerSnapshot.ownerName || '—'}</td>
                  <td className="p-3 text-sm font-bold text-slate-900 text-right">{fmtEur(s.totalBilled)}</td>
                  <td className="p-3">
                    {s.status === 'FINALIZED' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-800 text-white rounded-full text-xs font-bold">
                        <Lock className="w-3 h-3" /> Finalisé
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                        <FileText className="w-3 h-3" /> Brouillon
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-right space-x-3">
                    {s.status === 'FINALIZED' && s.pdfStoragePath ? (
                      <button
                        onClick={() => handleDownloadArchived(s)}
                        className="text-xs font-bold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
                      >
                        <Download className="w-3.5 h-3.5" /> Télécharger le PDF
                      </button>
                    ) : (
                      <button
                        onClick={() => setReviewStatement(s)}
                        className="text-xs font-bold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
                      >
                        <Eye className="w-3.5 h-3.5" /> Reprendre
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Drawer de revue de brouillon */}
      {reviewStatement && (
        <CrgDraftReview
          statement={reviewStatement}
          onClose={() => setReviewStatement(null)}
          onChanged={async updated => {
            setReviewStatement(updated);
            await loadData();
            if (view === 'history') await loadHistory();
          }}
          onDeleted={async () => {
            setReviewStatement(null);
            await loadData();
            if (view === 'history') await loadHistory();
          }}
          onRegenerate={async () => {
            if (!agencyId) return;
            const results = await rentalReadyService.generateCrg(
              agencyId, reviewStatement.periodYear, reviewStatement.periodMonth, [reviewStatement.propertyId]
            );
            const res = results[0];
            if (res?.statementId) {
              const fresh = await billingService.getCrgStatement(res.statementId);
              if (fresh) setReviewStatement(fresh);
              toast.success('Brouillon régénéré depuis RentalReady');
            } else {
              toast.error(res?.message || 'Échec de la régénération');
            }
            await loadData();
          }}
          onPreviewPdf={() => setPdfJob({ statement: reviewStatement, action: 'download' })}
          onFinalize={() => handleFinalize(reviewStatement)}
        />
      )}

      {/* Rendu PDF caché */}
      {pdfJob && (
        <div ref={pdfRef} style={{ position: 'fixed', left: '-10000px', top: 0, width: '210mm' }}>
          <CrgPdfDocument statement={pdfJob.statement} />
        </div>
      )}
    </div>
  );
};

// --- Drawer de revue d'un brouillon ---

const CrgDraftReview: React.FC<{
  statement: CrgStatement;
  onClose: () => void;
  onChanged: (updated: CrgStatement) => Promise<void>;
  onDeleted: () => Promise<void>;
  onRegenerate: () => Promise<void>;
  onPreviewPdf: () => void;
  onFinalize: () => void;
}> = ({ statement, onClose, onChanged, onDeleted, onRegenerate, onPreviewPdf, onFinalize }) => {
  const { toast, confirm } = useModal();
  const [lines, setLines] = useState<CrgLine[]>(statement.lines);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualLabel, setManualLabel] = useState('');
  const [manualAmount, setManualAmount] = useState('');

  useEffect(() => { setLines(statement.lines); }, [statement.id, statement.lines]);

  const totals = computeCrgTotals(lines, statement.rebillCleaning);
  const isDirty = JSON.stringify(lines) !== JSON.stringify(statement.lines);

  const toggleExcluded = (index: number) => {
    setLines(prev => prev.map((l, i) => (i === index ? { ...l, excluded: !l.excluded } : l)));
  };

  const removeManualLine = (index: number) => {
    setLines(prev => prev.filter((_, i) => i !== index));
  };

  const addManualLine = () => {
    const amount = parseFloat(manualAmount.replace(',', '.'));
    if (!manualLabel.trim() || isNaN(amount)) {
      toast.error('Renseignez un libellé et un montant valide');
      return;
    }
    const commissionAmount = Math.round(amount * statement.commissionRate) / 100;
    const today = new Date().toISOString().slice(0, 10);
    setLines(prev => [...prev, {
      rrReservationId: null,
      source: 'Manuel',
      guestName: manualLabel.trim(),
      checkinDate: today,
      checkoutDate: today,
      rrStatus: 'MANUAL',
      isCancellationFee: false,
      paidOut: amount,
      paymentFees: 0,
      cityTax: 0,
      cleaningFee: 0,
      netRevenue: amount,
      commissionRate: statement.commissionRate,
      commissionAmount,
      excluded: false,
      manual: true,
      note: manualLabel.trim(),
    }]);
    setManualLabel('');
    setManualAmount('');
    setShowManualForm(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await billingService.updateDraftLines(statement.id, lines);
      if (updated) {
        toast.success('Brouillon enregistré');
        await onChanged(updated);
      }
    } catch (e: any) {
      toast.error('Erreur : ' + (e.message || 'Erreur inconnue'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Supprimer ce brouillon ?',
      message: 'Le brouillon de CRG sera supprimé. Vous pourrez le régénérer plus tard. Continuer ?',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
    });
    if (!ok) return;
    try {
      await billingService.deleteDraft(statement.id);
      toast.success('Brouillon supprimé');
      await onDeleted();
    } catch (e: any) {
      toast.error('Erreur : ' + (e.message || 'Erreur inconnue'));
    }
  };

  const handleRegenerate = async () => {
    const ok = await confirm({
      title: 'Régénérer depuis RentalReady ?',
      message: 'Les lignes seront remplacées par les données actuelles de RentalReady. Les exclusions et lignes manuelles seront perdues. Continuer ?',
      confirmText: 'Régénérer',
      cancelText: 'Annuler',
    });
    if (!ok) return;
    setRegenerating(true);
    try {
      await onRegenerate();
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50 shrink-0">
          <div>
            <h3 className="text-lg font-bold text-slate-900">
              CRG — {statement.headerSnapshot.propertyName || statement.headerSnapshot.propertyAddress} · {MONTHS_FR[statement.periodMonth - 1]} {statement.periodYear}
            </h3>
            <p className="text-sm text-slate-500">
              {statement.headerSnapshot.ownerName} · Taux {statement.commissionRate} % · Ménage {statement.rebillCleaning ? 'refacturé' : 'non refacturé'}
            </p>
          </div>
          <button onClick={onClose} className="hover:bg-slate-200 rounded-full p-1 transition-colors">
            <X className="w-6 h-6 text-slate-400" />
          </button>
        </div>

        {/* Lignes */}
        <div className="flex-1 overflow-y-auto p-5">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="p-2 text-left text-[10px] font-bold text-slate-500 uppercase">Inclure</th>
                <th className="p-2 text-left text-[10px] font-bold text-slate-500 uppercase">Plateforme</th>
                <th className="p-2 text-left text-[10px] font-bold text-slate-500 uppercase">Voyageur</th>
                <th className="p-2 text-left text-[10px] font-bold text-slate-500 uppercase">Arrivée</th>
                <th className="p-2 text-left text-[10px] font-bold text-slate-500 uppercase">Départ</th>
                <th className="p-2 text-right text-[10px] font-bold text-slate-500 uppercase">Versé</th>
                <th className="p-2 text-right text-[10px] font-bold text-slate-500 uppercase">Ménage</th>
                <th className="p-2 text-right text-[10px] font-bold text-slate-500 uppercase">CA NET</th>
                <th className="p-2 text-right text-[10px] font-bold text-slate-500 uppercase">Commission</th>
                <th className="p-2 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lines.length === 0 && (
                <tr>
                  <td colSpan={10} className="p-6 text-center text-slate-400 text-sm">
                    Aucune réservation sur la période. Le CRG peut tout de même être finalisé (mois sans activité).
                  </td>
                </tr>
              )}
              {lines.map((line, i) => (
                <tr key={i} className={line.excluded ? 'opacity-40' : ''}>
                  <td className="p-2">
                    <input
                      type="checkbox"
                      checked={!line.excluded}
                      onChange={() => toggleExcluded(i)}
                      className="w-4 h-4 text-emerald-600 border-slate-300 rounded"
                    />
                  </td>
                  <td className="p-2 text-xs text-slate-700">{line.source}</td>
                  <td className="p-2 text-xs text-slate-900 font-medium">
                    {line.guestName}
                    {line.isCancellationFee && <span className="text-red-600 font-bold"> (annulation)</span>}
                    {line.manual && <span className="text-slate-400 italic"> (manuel)</span>}
                  </td>
                  <td className="p-2 text-xs text-slate-600">{fmtDate(line.checkinDate)}</td>
                  <td className="p-2 text-xs text-slate-600">{fmtDate(line.checkoutDate)}</td>
                  <td className="p-2 text-xs text-slate-900 text-right">
                    {fmtEur(line.paidOut)}
                    {(line.cityTax || 0) > 0 && (
                      <span className="block text-[10px] text-slate-400" title="Taxe de séjour reversée par la plateforme, incluse dans le versé">
                        dont taxe séjour {fmtEur(line.cityTax!)}
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-xs text-slate-900 text-right">{fmtEur(line.cleaningFee)}</td>
                  <td className="p-2 text-xs text-slate-900 text-right">{fmtEur(line.netRevenue)}</td>
                  <td className="p-2 text-xs font-bold text-slate-900 text-right">{fmtEur(line.commissionAmount)}</td>
                  <td className="p-2">
                    {line.manual && (
                      <button onClick={() => removeManualLine(i)} className="text-red-400 hover:text-red-600">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Ajout de ligne manuelle */}
          <div className="mt-4">
            {showManualForm ? (
              <div className="flex items-end gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="flex-1">
                  <label className="block text-xs font-bold text-slate-500 mb-1">Libellé</label>
                  <input
                    className="w-full p-2 border border-slate-300 rounded-lg text-sm"
                    placeholder="Ex : Pénalité annulation hors plateforme"
                    value={manualLabel}
                    onChange={e => setManualLabel(e.target.value)}
                  />
                </div>
                <div className="w-40">
                  <label className="block text-xs font-bold text-slate-500 mb-1">Montant (base commission)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full p-2 border border-slate-300 rounded-lg text-sm"
                    placeholder="0.00"
                    value={manualAmount}
                    onChange={e => setManualAmount(e.target.value)}
                  />
                </div>
                <button onClick={addManualLine} className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-bold">Ajouter</button>
                <button onClick={() => setShowManualForm(false)} className="px-3 py-2 text-slate-500 text-sm">Annuler</button>
              </div>
            ) : (
              <button
                onClick={() => setShowManualForm(true)}
                className="text-sm font-bold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Ajouter une ligne manuelle
              </button>
            )}
          </div>

          {/* Totaux */}
          <div className="mt-6 flex justify-end">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 min-w-[280px] space-y-1">
              <div className="flex justify-between text-sm"><span className="text-slate-600">Total versé au client</span><span className="font-bold">{fmtEur(totals.totalPaidOut)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-600">Total frais de ménage</span><span className="font-bold">{fmtEur(totals.totalCleaning)}</span></div>
              {totals.totalCityTax > 0 && (
                <div className="flex justify-between text-sm"><span className="text-slate-600">Taxe de séjour (non commissionnée)</span><span className="font-bold">{fmtEur(totals.totalCityTax)}</span></div>
              )}
              <div className="flex justify-between text-sm"><span className="text-slate-600">Total CA NET</span><span className="font-bold">{fmtEur(totals.totalNetRevenue)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-600">Total commission ({statement.commissionRate} %)</span><span className="font-bold">{fmtEur(totals.totalCommission)}</span></div>
              {statement.rebillCleaning && (
                <div className="flex justify-between text-sm"><span className="text-slate-600">Refacturation ménage</span><span className="font-bold">{fmtEur(totals.totalCleaning)}</span></div>
              )}
              <div className="flex justify-between text-base pt-2 border-t border-slate-300">
                <span className="font-bold text-slate-900">Total à facturer</span>
                <span className="font-extrabold text-emerald-700">{fmtEur(totals.totalBilled)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={handleRegenerate}
              disabled={regenerating}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-bold hover:bg-white flex items-center gap-1.5 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${regenerating ? 'animate-spin' : ''}`} /> Régénérer depuis RentalReady
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 border border-red-200 text-red-600 rounded-lg text-sm font-bold hover:bg-red-50 flex items-center gap-1.5"
            >
              <Trash2 className="w-4 h-4" /> Supprimer
            </button>
          </div>
          <div className="flex items-center gap-3">
            {isDirty && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-bold flex items-center gap-1.5 disabled:opacity-50"
              >
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : null} Enregistrer les modifications
              </button>
            )}
            <button
              onClick={onPreviewPdf}
              disabled={isDirty}
              title={isDirty ? 'Enregistrez d\'abord les modifications' : undefined}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-bold hover:bg-white flex items-center gap-1.5 disabled:opacity-50"
            >
              <Eye className="w-4 h-4" /> Aperçu PDF
            </button>
            <button
              onClick={onFinalize}
              disabled={isDirty}
              title={isDirty ? 'Enregistrez d\'abord les modifications' : undefined}
              className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold flex items-center gap-1.5 disabled:opacity-50"
            >
              <Lock className="w-4 h-4" /> Finaliser
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
